import { MarkerGroup, createHtmlMarker } from '../utils/markers';

var OVERLAP_PIXEL_RADIUS = 18;
var COMBINED_BADGE_PROXIMITY_PX = 30;

export function createOverlapManager(config) {
    var state = config.state;

    function overlapSortValue(feature, field) {
        var p = feature.properties || {};
        if (field === 'tran_wersja_id') return p.dok_date || p.dok_data || p.tran_wersja_id || '';
        if (field === 'cena_brutto') return p.cena_brutto != null ? Number(p.cena_brutto) : -Infinity;
        if (field === 'price_per_m2') return p.price_per_m2 != null ? Number(p.price_per_m2) : -Infinity;
        if (field === 'powierzchnia') return p.powierzchnia != null ? Number(p.powierzchnia) : -Infinity;
        return '';
    }

    function sortOverlapFeatures(features, sort) {
        var field = config.sortField(sort);
        var dir = config.sortDir(sort);
        var sorted = features.slice();
        sorted.sort(function(a, b) {
            var va = overlapSortValue(a, field);
            var vb = overlapSortValue(b, field);
            if (va < vb) return dir === 'DESC' ? 1 : -1;
            if (va > vb) return dir === 'DESC' ? -1 : 1;
            return 0;
        });
        return sorted;
    }

    function clearCombinedBadgeLayer() {
        var combinedBadgeLayer = state.getCombinedBadgeLayer();
        if (combinedBadgeLayer) {
            combinedBadgeLayer.remove();
            state.setCombinedBadgeLayer(null);
        }
    }

    function clearOverlapLayer() {
        state.setOverlapLayer(null);
        state.setOverlapGroupsCache([]);
        clearCombinedBadgeLayer();
    }

    function computeOverlapGroups(features) {
        var map = config.getMap();
        var items = [];
        features.forEach(function(feature) {
            var centroid = config.getFeatureCentroid(feature);
            if (!centroid) return;
            var point = map.project([centroid.lng, centroid.lat]);
            items.push({ feature: feature, latlng: centroid, px: point.x, py: point.y, group: -1 });
        });

        var groupId = 0;
        var rawGroups = {};
        var radiusSquared = OVERLAP_PIXEL_RADIUS * OVERLAP_PIXEL_RADIUS;
        for (var i = 0; i < items.length; i++) {
            if (items[i].group >= 0) continue;
            items[i].group = groupId;
            var stack = [i];
            while (stack.length) {
                var currentIndex = stack.pop();
                for (var j = i + 1; j < items.length; j++) {
                    if (items[j].group >= 0) continue;
                    var dx = items[currentIndex].px - items[j].px;
                    var dy = items[currentIndex].py - items[j].py;
                    if (dx * dx + dy * dy <= radiusSquared) {
                        items[j].group = groupId;
                        stack.push(j);
                    }
                }
            }
            groupId++;
        }

        items.forEach(function(item) {
            if (!rawGroups[item.group]) rawGroups[item.group] = { features: [], sumLat: 0, sumLng: 0 };
            var group = rawGroups[item.group];
            group.features.push(item.feature);
            group.sumLat += item.latlng.lat;
            group.sumLng += item.latlng.lng;
        });

        var result = [];
        Object.keys(rawGroups).forEach(function(gid) {
            var group = rawGroups[gid];
            if (group.features.length < 2) return;
            var count = group.features.length;
            result.push({
                center: { lat: group.sumLat / count, lng: group.sumLng / count },
                features: group.features
            });
        });
        return result;
    }

    function refreshGroupsFromGeoJson(data) {
        clearOverlapLayer();
        state.setOverlapGroupsCache(data && data.aggregated === true
            ? []
            : computeOverlapGroups((data && data.features) || []));
    }

    function buildCombinedBadges() {
        var map = config.getMap();
        if (state.getOverlapLayer()) {
            state.setOverlapLayer(null);
        }
        config.clearGunbBadgeLayer();
        clearCombinedBadgeLayer();

        var badgeGroup = new MarkerGroup();
        var usedGunbKeys = {};

        state.getOverlapGroupsCache().forEach(function(transactionGroup) {
            var transactionCenter = transactionGroup.center;
            var transactionPoint = map.project([transactionCenter.lng, transactionCenter.lat]);
            var nearbyGunb = [];

            Object.keys(state.getGunbGroupsCache()).forEach(function(key) {
                if (usedGunbKeys[key]) return;
                var gunbFeatures = state.getGunbGroupsCache()[key];
                var gunbCentroid = config.getFeatureCentroid(gunbFeatures[0]);
                if (!gunbCentroid) return;
                var gunbPoint = map.project([gunbCentroid.lng, gunbCentroid.lat]);
                var dx = transactionPoint.x - gunbPoint.x;
                var dy = transactionPoint.y - gunbPoint.y;
                if (dx * dx + dy * dy <= COMBINED_BADGE_PROXIMITY_PX * COMBINED_BADGE_PROXIMITY_PX) {
                    nearbyGunb.push({ key: key, features: gunbFeatures, centroid: gunbCentroid });
                }
            });

            if (nearbyGunb.length > 0) {
                nearbyGunb.forEach(function(entry) { usedGunbKeys[entry.key] = true; });
                var mergedGunbFeatures = [];
                nearbyGunb.forEach(function(entry) {
                    mergedGunbFeatures = mergedGunbFeatures.concat(entry.features);
                });
                var transactionCount = transactionGroup.features.length;
                var gunbCount = mergedGunbFeatures.length;
                var capturedTransactionFeatures = transactionGroup.features;
                var capturedGunbFeatures = mergedGunbFeatures;
                var capturedCenter = transactionCenter;
                var marker = createHtmlMarker(
                    [transactionCenter.lng, transactionCenter.lat],
                    '<div class="nm-combined-badge">' +
                        '<span class="nm-combined-badge-t">' + transactionCount + '</span>' +
                        '<span class="nm-combined-badge-sep"></span>' +
                        '<span class="nm-combined-badge-g">' + gunbCount + '</span>' +
                    '</div>',
                    'nm-overlap-badge-wrap nm-overlap-badge-wrap--combined'
                );
                marker.getElement().addEventListener('click', function(event) {
                    event.stopPropagation();
                    browseBadgePoint(capturedCenter, capturedTransactionFeatures, capturedGunbFeatures);
                });
                badgeGroup.add(marker);
                return;
            }

            var simpleTransactionCount = transactionGroup.features.length;
            var simpleTransactionFeatures = transactionGroup.features;
            var simpleTransactionCenter = transactionCenter;
            var simpleMarker = createHtmlMarker(
                [transactionCenter.lng, transactionCenter.lat],
                '<div class="nm-overlap-badge"><span>' + simpleTransactionCount + '</span></div>',
                'nm-overlap-badge-wrap'
            );
            simpleMarker.getElement().addEventListener('click', function(event) {
                event.stopPropagation();
                browseBadgePoint(simpleTransactionCenter, simpleTransactionFeatures, []);
            });
            badgeGroup.add(simpleMarker);
        });

        Object.keys(state.getGunbGroupsCache()).forEach(function(key) {
            if (usedGunbKeys[key]) return;
            var features = state.getGunbGroupsCache()[key];
            if (features.length < 2) return;
            var centroid = config.getFeatureCentroid(features[0]);
            if (!centroid) return;
            var count = features.length;
            var capturedFeatures = features;
            var capturedCentroid = centroid;
            var badge = createHtmlMarker(
                [centroid.lng, centroid.lat],
                '<div class="nm-overlap-badge nm-overlap-badge--gunb"><span>' + count + '</span></div>',
                'nm-overlap-badge-wrap nm-overlap-badge-wrap--gunb'
            );
            badge.getElement().addEventListener('click', function(event) {
                event.stopPropagation();
                browseBadgePoint(capturedCentroid, [], capturedFeatures);
            });
            badgeGroup.add(badge);
        });

        if (badgeGroup.markers.length) {
            badgeGroup.addTo(map);
            state.setCombinedBadgeLayer(badgeGroup);
        }
    }

    // HTML badges are not included in queryRenderedFeatures. Seed the complete
    // group so every member is available, even away from its averaged anchor.
    // Starting a new point query also cancels results from the previous click.
    function browseBadgePoint(latlng, transactions, permits) {
        var hits = transactions.map(function(feature) {
            return {
                type: 'transaction',
                id: String(feature.properties.tran_lokalny_id_iip),
                properties: feature.properties,
                feature: feature,
            };
        }).concat(permits.map(function(feature) {
            return {
                type: 'gunb',
                id: String(feature.properties.id),
                properties: feature.properties,
                feature: feature,
            };
        }));
        config.dispatchPointQuery(latlng, config.getMap().project([latlng.lng, latlng.lat]), hits);
    }

    function renderOverlapBrowsePanel() {
        var sorted = sortOverlapFeatures(state.getOverlapBrowseFeatures(), config.getCurrentSort());
        config.listTitleEl.textContent = sorted.length + ' transakcji w tym miejscu';
        config.listMetaEl.textContent = 'Wybierz transakcję z listy, aby zobaczyć jej szczegóły';
        config.sortRowEl.style.display = '';
        config.listMoreBtn.style.display = 'none';

        var html = '<div class="nm-overlap-back-row">' +
            '<button class="ms-button ms-button--secondary ms-button--sm nm-overlap-back-btn" id="nm-overlap-back" type="button">← Wróć do pełnej listy</button>' +
        '</div>';
        html += '<div class="nm-list-stack">';
        sorted.forEach(function(feature) {
            html += config.renderListCardHtml((feature && feature.properties) || {});
        });
        html += '</div>';
        config.listContentEl.innerHTML = html;
        config.listContentEl.__listContentHtml = null;
        config.applySelectedStylesInList();
    }

    function browseOverlapInPanel(latlng, features) {
        config.clearTapBrowseState();
        state.setGunbBrowseFeatures(null);
        state.setGunbBrowseLatlng(null);
        state.setCombinedBrowseTransactions(null);
        state.setCombinedBrowseGunb(null);
        state.setCombinedBrowseOtherHits(null);
        state.setCombinedBrowseLatlng(null);
        state.setOverlapBrowseFeatures(features);
        state.setOverlapBrowseLatlng(latlng);
        config.renderListPanel();
        config.setTab('list', { snap: 'half' });
    }

    function exitOverlapBrowse() {
        state.setOverlapBrowseFeatures(null);
        state.setOverlapBrowseLatlng(null);
        config.renderListPanel({ immediate: true });
    }

    return {
        buildCombinedBadges: buildCombinedBadges,
        browseOverlapInPanel: browseOverlapInPanel,
        clearCombinedBadgeLayer: clearCombinedBadgeLayer,
        clearOverlapLayer: clearOverlapLayer,
        exitOverlapBrowse: exitOverlapBrowse,
        refreshGroupsFromGeoJson: refreshGroupsFromGeoJson,
        renderOverlapBrowsePanel: renderOverlapBrowsePanel,
        sortOverlapFeatures: sortOverlapFeatures,
    };
}
